import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { usePlayback } from '../context/playback';
import { trackEvent } from '../analytics';
import api from '../api';
import { mediaToQueueItem } from '../utils/queueItems';

/**
 * Builds queue items from an array of posts.
 * Each item includes ISRC/UPC for lazy preview fetching. A list has no media
 * of its own: it plays as its items, in list order, each credited to its own
 * item post so its card shows it playing and the play counts for it. It plays
 * the items it has loaded (see loadFullLists).
 */
export function postsToQueueItems(posts) {
  var queue = [];
  posts.forEach(function (p) {
    if (p.media) {
      queue.push(mediaToQueueItem(p.id, p.media));
    } else if (p.kind === 'list' && p.list) {
      var items = (p.list.items && p.list.items.length ? p.list.items : p.list.preview) || [];
      items
        .slice()
        .sort(function (a, b) {
          return a.position - b.position;
        })
        .forEach(function (item) {
          if (item.media) queue.push(mediaToQueueItem(item.id || p.id, item.media));
        });
    }
  });
  return queue;
}

/**
 * Feed and bookmark responses carry only a list's preview (its first few
 * items), so before playing, a list whose preview is shorter than the list
 * is fetched whole. In parallel; a list whose fetch fails plays its preview.
 */
export function loadFullLists(posts) {
  return Promise.all(
    posts.map(function (p) {
      if (p.kind !== 'list' || !p.list) return p;
      var loaded = (p.list.items && p.list.items.length) || (p.list.preview || []).length;
      if (loaded >= p.list.itemCount) return p;
      return api
        .get('/api/posts/id/' + p.id)
        .then(function (res) {
          return res && res.data && res.data.list ? res.data : p;
        })
        .catch(function () {
          return p;
        });
    })
  );
}

var SEEN_KEY = 'hr_seen_play_all_explainer';
var DISMISS_CARD_KEY = 'play_all_explainer';

var MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1100;
`;

var MenuPopup = styled.div`
  position: fixed;
  background: white;
  color: black;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 4px 0;
  z-index: 1101;
  min-width: 180px;
`;

var MenuItem = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 8px 12px;
  font-size: 0.8rem;
  color: black;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;

  &:hover {
    background: #f0f0f0;
  }
`;

/**
 * A small play icon button that queues and plays all posts.
 * Shows a one-time explainer on first tap.
 * Props:
 *   posts: array of post objects with media
 *   source: string for analytics (e.g., 'feed_all', 'bookmarks')
 *   loading: boolean - disable while loading
 */
export default function PlayAllButton(props) {
  var playback = usePlayback();
  var posts = props.posts || [];
  var loading = props.loading || false;
  var source = props.source || 'unknown';
  var disabled = loading || posts.length === 0;
  var btnRef = useRef(null);

  var _showExplainer = useState(false);
  var showExplainer = _showExplainer[0];
  var setShowExplainer = _showExplainer[1];

  var _menuPos = useState(null);
  var menuPos = _menuPos[0];
  var setMenuPos = _menuPos[1];

  var isSomethingPlaying =
    playback.playbackState === 'playing' || playback.playbackState === 'paused';

  function getButtonPos() {
    if (!btnRef.current) return { x: 0, y: 0 };
    var rect = btnRef.current.getBoundingClientRect();
    return { x: rect.left, y: rect.bottom + 4 };
  }

  function doPlayNow() {
    loadFullLists(posts).then(function (full) {
      var items = postsToQueueItems(full);
      if (items.length > 0) {
        trackEvent('play_all', { source: source, count: items.length, action: 'play_now' });
        playback.playNow(items);
      }
    });
  }

  function doAddToQueue() {
    setMenuPos(null);
    loadFullLists(posts).then(function (full) {
      var items = postsToQueueItems(full);
      if (items.length > 0) {
        trackEvent('play_all', { source: source, count: items.length, action: 'add_to_queue' });
        playback.addToQueue(items);
      }
    });
  }

  var dismissedCards = props.dismissedCards || [];

  function hasSeen() {
    if (dismissedCards.indexOf(DISMISS_CARD_KEY) !== -1) return true;
    try {
      return !!localStorage.getItem(SEEN_KEY);
    } catch (e) {
      return false;
    }
  }

  function markSeen() {
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch (e) {}
    api.post('/api/users/dismiss-card', { key: DISMISS_CARD_KEY }).catch(function () {});
  }

  function dismissAndContinue() {
    markSeen();
    setShowExplainer(false);
    if (isSomethingPlaying) {
      setMenuPos(getButtonPos());
    } else {
      doPlayNow();
    }
  }

  function dismissOnly() {
    markSeen();
    setShowExplainer(false);
  }

  function handleClick() {
    if (!hasSeen()) {
      setShowExplainer(true);
      return;
    }
    if (isSomethingPlaying) {
      setMenuPos(getButtonPos());
    } else {
      doPlayNow();
    }
  }

  // Clamp menu so it doesn't go off-screen
  var menuStyle = menuPos
    ? {
        left: Math.min(menuPos.x, window.innerWidth - 190),
        top: menuPos.y,
      }
    : {};

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleClick}
        disabled={disabled}
        style={{
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '6px',
          cursor: disabled ? 'default' : 'pointer',
          padding: 0,
          width: '28px',
          height: '28px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? 0.3 : 1,
        }}
        aria-label="Play all"
      >
        <svg width="14" height="14" viewBox="0 0 24 24">
          <path d="M6 4v16l14-8z" fill="#000" />
        </svg>
      </button>
      {menuPos && (
        <>
          <MenuOverlay
            onClick={function () {
              setMenuPos(null);
            }}
          />
          <MenuPopup style={menuStyle}>
            <MenuItem
              onClick={function () {
                setMenuPos(null);
                doPlayNow();
              }}
            >
              Play Now
            </MenuItem>
            <MenuItem
              onClick={function () {
                doAddToQueue();
              }}
            >
              Add to Queue
            </MenuItem>
          </MenuPopup>
        </>
      )}
      {showExplainer && (
        <div
          onClick={dismissOnly}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={function (e) {
              e.stopPropagation();
            }}
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 24,
              width: '90%',
              maxWidth: 320,
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <button
              onClick={dismissOnly}
              style={{
                position: 'absolute',
                top: '0.75rem',
                right: '0.75rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.1rem',
                color: '#999',
                lineHeight: 1,
                padding: 0,
              }}
              aria-label="Close"
            >
              &times;
            </button>
            <div style={{ marginBottom: 12 }}>
              <svg width="32" height="32" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="11" fill="none" stroke="#333" strokeWidth="1.5" />
                <path d="M9.5 7.5v9l7-4.5z" fill="#333" />
              </svg>
            </div>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 8 }}>Play All</div>
            <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.5, marginBottom: 16 }}>
              This button will play all of the visible tracks and albums in the feed.
            </div>
            <button
              onClick={dismissAndContinue}
              style={{
                background: 'black',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: '0.85rem',
                fontFamily: 'inherit',
                padding: '0.55em 1.5em',
                cursor: 'pointer',
              }}
            >
              Got it, play!
            </button>
          </div>
        </div>
      )}
    </>
  );
}
